import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon,
  Public as PublicIcon,
  QrCode as QrCodeIcon,
  Email as EmailIcon,
  Star as StarIcon,
  Payment as PaymentIcon,
  Visibility as VisibilityIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';

const Features = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const features = [
    {
      icon: <ScheduleIcon sx={{ fontSize: 40, color: '#f5f5dc' }} />,
      title: t('features.smartCalendar'),
      description: t('features.smartCalendarDesc'),
      details: [
        t('featuresPage.intuitiveCalendar'),
        t('featuresPage.slotManagement'),
        t('featuresPage.realTimeSync'),
        t('featuresPage.autoNotifications')
      ]
    },
    {
      icon: <PublicIcon sx={{ fontSize: 40, color: '#f5f5dc' }} />,
      title: t('features.publicProfile'),
      description: t('features.publicProfileDesc'),
      details: [
        t('featuresPage.googleSEO'),
        t('featuresPage.customProfile'),
        t('featuresPage.detailedPhotos'),
        t('featuresPage.visibleContact')
      ]
    },
    {
      icon: <QrCodeIcon sx={{ fontSize: 40, color: '#f5f5dc' }} />,
      title: t('features.customQR'),
      description: t('features.customQRDesc'),
      details: [
        t('featuresPage.uniqueQR'),
        t('featuresPage.directBooking'),
        t('featuresPage.simplifiedReviews'),
        t('featuresPage.customDisplay')
      ]
    },
    {
      icon: <EmailIcon sx={{ fontSize: 40, color: '#f5f5dc' }} />,
      title: t('features.autoReminders'),
      description: t('features.autoRemindersDesc'),
      details: [
        t('featuresPage.reminder24h'),
        t('featuresPage.bookingConfirmations'),
        t('featuresPage.personalizedFollowup'),
        t('featuresPage.reducedCancellations')
      ]
    },
    {
      icon: <StarIcon sx={{ fontSize: 40, color: '#f5f5dc' }} />,
      title: t('features.seoBoost'),
      description: t('features.seoBoostDesc'),
      details: [
        t('featuresPage.autoInvitations'),
        t('featuresPage.localSEOImprovement'),
        t('featuresPage.onlineReputationManagement'),
        t('featuresPage.increasedVisibility')
      ]
    },
    {
      icon: <PaymentIcon sx={{ fontSize: 40, color: '#f5f5dc' }} />,
      title: t('features.securePayment'),
      description: t('features.securePaymentDesc'),
      details: [
        t('featuresPage.stripePayments'),
        t('featuresPage.servicePrePayment'),
        t('featuresPage.refundManagement'),
        t('featuresPage.autoBilling')
      ]
    },
    {
      icon: <VisibilityIcon sx={{ fontSize: 40, color: '#f5f5dc' }} />,
      title: t('features.maxVisibility'),
      description: t('features.maxVisibilityDesc'),
      details: [
        t('featuresPage.platformListing'),
        t('featuresPage.geoSearch'),
        t('featuresPage.categoryFilters'),
        t('featuresPage.newClientExposure')
      ]
    }
  ];

  return (
    <Box>
      <AppBar position="static" sx={{ backgroundColor: 'white', color: 'black', boxShadow: 1 }}>
        <Toolbar>
          <IconButton onClick={() => navigate('/')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            {t('featuresPage.bookingbarberFeatures')}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box textAlign="center" mb={6}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
            {t('featuresPage.allFeatures')}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            {t('featuresPage.discoverTools')}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%', border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box display="flex" alignItems="center" mb={3}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        backgroundColor: '#212121',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Box>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <List dense>
                    {feature.details.map((detail, detailIndex) => (
                      <ListItem key={detailIndex} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={detail} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box mt={6} textAlign="center">
          <Paper elevation={0} sx={{ p: 4, border: '1px solid #e0e0e0', backgroundColor: '#f5f5dc' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('featuresPage.readyToStart')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('featuresPage.allFeaturesIncluded')}
            </Typography>
            <Box sx={{ mt: 3 }}>
              <button
                onClick={() => navigate('/auth?mode=signup')}
                style={{
                  backgroundColor: '#212121',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginRight: '16px'
                }}
              >
                {t('cta.startFree')}
              </button>
              <button
                onClick={() => navigate('/pricing')}
                style={{
                  backgroundColor: 'transparent',
                  color: '#212121',
                  border: '2px solid #212121',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {t('featuresPage.viewPricing')}
              </button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Features;